// src/components/ManageOrderSheets.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  List,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  NumberOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const { Title, Text } = Typography;

const ManageOrderSheets = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sheets, setSheets] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // slice for current page
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const visible = useMemo(() => sheets.slice(start, end), [sheets, start, end]);

  const tokenHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const refreshSheets = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/admin/order-sheets/recent?limit=200", {
        headers: tokenHeader(),
      });
      const rows = Array.isArray(data?.sheets) ? data.sheets : [];
      setSheets(rows);

      // clamp page if list shrank
      const maxPage = Math.max(1, Math.ceil(rows.length / pageSize));
      if (page > maxPage) setPage(maxPage);

      // drop selections that no longer exist
      setSelectedIds((prev) => {
        const next = new Set([...prev].filter((id) => rows.some((r) => r.id === id)));
        return next;
      });
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load order sheet archive.");
      setSheets([]);
      setPage(1);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadSheet = async (sheetId, fileName) => {
    try {
      const resp = await axiosInstance.get(`/admin/order-sheets/${sheetId}/download`, {
        headers: tokenHeader(),
        responseType: "blob",
      });
      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `OrderSheet-${sheetId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e?.response?.data?.message || "Download failed.");
    }
  };

  const deleteSheet = async (sheetId) => {
    try {
      await axiosInstance.delete(`/admin/order-sheets/${sheetId}`, {
        headers: tokenHeader(),
      });
      message.success(`Order sheet #${sheetId} deleted`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(sheetId);
        return next;
      });
      await refreshSheets();
    } catch (e) {
      message.error(e?.response?.data?.message || "Delete failed.");
    }
  };

  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await axiosInstance.post(
        "/admin/order-sheets/bulk-delete",
        { sheetNumbers: ids },
        { headers: { "Content-Type": "application/json", ...tokenHeader() } }
      );
      message.success(`Deleted ${ids.length} sheet${ids.length > 1 ? "s" : ""}`);
      setSelectedIds(new Set());
      await refreshSheets();
    } catch (e) {
      message.error(e?.response?.data?.message || "Bulk delete failed.");
    }
  };

  // selection helpers
  const isRowChecked = (id) => selectedIds.has(id);
  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const pageAllSelected = visible.length > 0 && visible.every((r) => selectedIds.has(r.id));
  const pageAnySelected = visible.some((r) => selectedIds.has(r.id));
  const toggleSelectAllOnPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) visible.forEach((r) => next.add(r.id));
      else visible.forEach((r) => next.delete(r.id));
      return next;
    });
  };

  // stats
  const totalSheets = sheets.length;
  const selectedCount = selectedIds.size;
  const archivedCount = sheets.filter((s) => s.status === "archived").length;
  const issuedCount = sheets.filter((s) => s.status === "issued").length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/25 p-6">
      <div className="max-w-6xl mx-auto">
        <Card
          bordered={false}
          className="rounded-xl shadow-sm bg-white"
          title={
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <FileTextOutlined className="text-white" />
              </div>
              <Title level={4} style={{ margin: 0 }}>
                Manage Order Sheets
              </Title>
            </div>
          }
          extra={
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate("/production-dashboard")}
              >
                Back to Dashboard
              </Button>
            </Space>
          }
        >
          {/* Top Stats — compact and consistent */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" className="rounded-lg">
                <Statistic
                  title="Total Sheets"
                  value={totalSheets}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" className="rounded-lg">
                <Statistic
                  title="Issued"
                  value={issuedCount}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" className="rounded-lg">
                <Statistic
                  title="Archived"
                  value={archivedCount}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" className="rounded-lg">
                <Statistic
                  title="Selected"
                  value={selectedCount}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Action Bar */}
          <Card
            size="small"
            className="rounded-lg border-dashed"
            style={{ marginBottom: 16 }}
          >
            <Row align="middle" gutter={[16, 16]}>
              <Col flex="auto">
                <Space size="large" align="center">
                  <Checkbox
                    indeterminate={!pageAllSelected && pageAnySelected}
                    checked={pageAllSelected}
                    onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                  >
                    Select page ({visible.length})
                  </Checkbox>

                  <Input
                    size="small"
                    readOnly
                    style={{ width: 220 }}
                    prefix={<NumberOutlined />}
                    value={`Showing ${Math.min(sheets.length, pageSize)} of ${sheets.length}`}
                  />
                </Space>
              </Col>

              <Col>
                <Space wrap>
                  <Popconfirm
                    title={
                      selectedCount
                        ? `Delete ${selectedCount} selected sheet${selectedCount > 1 ? "s" : ""}?`
                        : "No sheets selected"
                    }
                    okText="Delete"
                    okButtonProps={{ danger: true, disabled: selectedCount === 0 }}
                    onConfirm={bulkDelete}
                    disabled={selectedCount === 0}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={selectedCount === 0}
                    >
                      Delete Selected ({selectedCount})
                    </Button>
                  </Popconfirm>

                  <Button icon={<ReloadOutlined />} onClick={refreshSheets}>
                    Refresh
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* List */}
          <Spin spinning={loading} size="large">
            {sheets.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <FileTextOutlined className="text-2xl text-gray-400" />
                </div>
                <Text type="secondary">No order sheets found</Text>
                <div style={{ marginTop: 12 }}>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={refreshSheets}>
                    Refresh
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Card
                  size="small"
                  className="rounded-lg"
                  bodyStyle={{ padding: 0 }}
                >
                  <List
                    dataSource={visible}
                    renderItem={(item) => {
                      const created = item.createdAt ? new Date(item.createdAt) : null;
                      const updated = item.updatedAt ? new Date(item.updatedAt) : null;
                      const status = String(item.status || "issued");
                      const hasNotes = !!item.hasNotes;

                      const statusConfig = {
                        archived: {
                          color: "default",
                          icon: <FileTextOutlined />,
                          label: "Archived",
                        },
                        issued: {
                          color: "blue",
                          icon: <CheckCircleOutlined />,
                          label: "Issued",
                        },
                      };
                      const statusInfo = statusConfig[status] || statusConfig.issued;

                      return (
                        <List.Item
                          className="hover:bg-gray-50 transition-colors"
                          style={{
                            padding: "14px 16px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {/* Left rail (consistent alignment) */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              width: 72,
                              minWidth: 72,
                              gap: 12,
                            }}
                          >
                            <Checkbox
                              checked={isRowChecked(item.id)}
                              onChange={(e) => toggleRow(item.id, e.target.checked)}
                            />
                            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                              <FileTextOutlined className="text-blue-600" />
                            </div>
                          </div>

                          {/* Main content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="flex items-center gap-8 flex-wrap">
                              <Space size="small" align="center" wrap>
                                <Text strong>Order Sheet #{item.id}</Text>
                                <Tag
                                  icon={statusInfo.icon}
                                  color={statusInfo.color}
                                  style={{ borderRadius: 999 }}
                                >
                                  {statusInfo.label}
                                </Tag>
                                {hasNotes && (
                                  <Badge dot color="gold">
                                    <Tag color="gold" style={{ borderRadius: 999 }}>
                                      Has Notes
                                    </Tag>
                                  </Badge>
                                )}
                                {item.fileName ? (
                                  <Tag>{item.fileName}</Tag>
                                ) : (
                                  <Tag color="default">no file</Tag>
                                )}
                              </Space>
                            </div>

                            <div className="text-gray-600" style={{ marginTop: 6, fontSize: 13 }}>
                              <Space size="large" wrap>
                                <span>
                                  <Text type="secondary">Issued:</Text>{" "}
                                  {created ? created.toLocaleString() : "—"}
                                </span>
                                {updated && (
                                  <span>
                                    <Text type="secondary">Updated:</Text>{" "}
                                    {updated.toLocaleString()}
                                  </span>
                                )}
                              </Space>
                            </div>

                            {Array.isArray(item.order_ids) && item.order_ids.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  ORDERS:
                                </Text>
                                <div className="flex flex-wrap gap-6 mt-1">
                                  <Text style={{ fontSize: 12 }}>
                                    {item.order_ids.slice(0, 12).join(", ")}
                                    {item.order_ids.length > 12 ? "…" : ""}
                                  </Text>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Actions — consistent sizing */}
                          <Space align="center">
                            <Tooltip title="Download">
                              <Button
                                type="text"
                                icon={<DownloadOutlined />}
                                onClick={() =>
                                  downloadSheet(
                                    item.id,
                                    item.fileName || `OrderSheet-${item.id}`
                                  )
                                }
                              />
                            </Tooltip>
                            <Popconfirm
                              title={`Delete sheet #${item.id}?`}
                              okText="Delete"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => deleteSheet(item.id)}
                            >
                              <Button type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </Space>
                        </List.Item>
                      );
                    }}
                  />
                </Card>

                {/* Pagination bar */}
                <div className="mt-4 flex items-center justify-between">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Showing {start + 1}-{Math.min(end, sheets.length)} of {sheets.length} sheets
                  </Text>
                  <Pagination
                    current={page}
                    total={sheets.length}
                    pageSize={pageSize}
                    onChange={(p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    }}
                    showSizeChanger
                    pageSizeOptions={["10", "20", "50", "100"]}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </div>
              </>
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default ManageOrderSheets;
